import Menu from './src/modules/Menu';
import leaderboard from './src/modules/Leaderboard';
import levelCreator from './src/libs/levelCreator';
import music from './src/modules/Music';
import arcadeFont from './src/assets/fonts/press-start-2p.woff2';
import {lockLandscape} from './src/modules/RotatePrompt';

// the game's code, asked for once and shared from then on
// (src/modules/gameChunk.js)
let chunk = null;

/**
 * The game chunk: PixiJS, the loading screen, the art preloader and
 * everything else the start screen has no use for. Kept out of the first
 * download so the start screen paints on its own code, and asked for as soon
 * as it is up, so the wait at START is usually none.
 * @returns {Promise<Object>} the chunk's exports
 */
function gameCode() {
  if (!chunk) {
    chunk = import(/* webpackChunkName: "game" */ './src/modules/gameChunk');
  }
  return chunk;
}

/**
 * Starts the art downloading behind the start screen, once the game chunk is
 * in (src/modules/Preload.js). A dog picked while the chunk was still coming
 * is taken up here, so no pick is lost.
 * @param {Object} game the chunk's exports
 * @param {String|null} dog the dog picked so far, if any
 * @returns {Object} the preloader
 */
function startArt(game, dog) {
  const art = game.startPreload();
  art.shared();
  if (dog) {
    art.dog(dog);
    game.pickWalkStrip(dog);
  }
  return art;
}

/**
 * Home screen first, then one game; the end panel's Play again reloads to the
 * home screen with the name, email and dog remembered. A finished game leaves
 * PixiJS' renderer and a pile of timelines behind, so a reload is the clean
 * way back. The home screen opens the run (start_run) before it resolves, and
 * its token goes on the leaderboard client for the one submit at the end.
 */
function goHome() {
  // the art downloads behind the start screen, once the game chunk is in and
  // the screen has what it needs itself, so START mostly finds it in
  let art = null;
  let picked = null;
  // the loading screen, made on START and handed to the game
  let loading = Promise.resolve(null);
  const game = gameCode().then((loaded) => {
    art = startArt(loaded, picked);
    return loaded;
  });
  Menu.show({
    onDog: (dog) => {
      picked = dog;
      if (!art) {
        return; // still coming: startArt takes this pick up when it lands
      }
      art.dog(dog);
      // the loading screen's walking dog, once the pick settles, so it never
      // waits for its own art
      game.then((loaded) => loaded.pickWalkStrip(dog));
    },
    // START: the loading screen fades in over the start screen, which then goes
    cover: (player) => {
      loading = game.then((loaded) => new loaded.LoadingScreen(player.dog));
      return loading.then((screen) => screen.show());
    }
  }).then((player) => {
    // START asked for fullscreen on a phone; now the run is on, hold it sideways
    // where the browser allows (Chrome on Android). Elsewhere the rotate prompt
    // covers a phone held upright. Done before the chunk is awaited: the
    // browser grants this off the back of the START tap.
    lockLandscape();
    return Promise.all([game, loading, player]);
  }).then(([loaded, screen, player]) => {
    // Game.load asks for the picked dog itself; no other dog starts from here
    art.stop();
    startGame(loaded, player, screen);
  });
}

function backToStart() {
  // the theme carries on from where it was on the start screen after the reload
  music.handOff();
  window.location = window.location.pathname;
}

/**
 * @param {Object} game the game chunk's exports
 * @param {Object} player
 * @param {LoadingScreen} [loading] the loading screen START put up, which the
 *   game takes down once its art is in and its first frame is drawn
 */
function startGame(game, player, loading) {
  // The home screen already opened the run. A custom level is a different
  // game, so it doesn't go on the board, and neither does a nameless run
  // from the ?dog= shortcut (no token). A start that played on without a
  // token may still be answered late; the leaderboard then keeps that token.
  if (!player.name || levelCreator.urlContainsLevelData()) {
    leaderboard.forgetRun();
  } else if (player.token) {
    leaderboard.runToken = player.token;
  }

  const running = new game.Game({
    spritesheet: 'sprites.json',
    dog: player.dog,
    loading,
    // Play again and quitting both reload to the start screen, where the details
    // are remembered; quitting submits nothing (Game.goHome)
    onPlayAgain: backToStart,
    onHome: backToStart
  });
  // A test hook for the Playwright run-through (test/e2e/run-through.mjs),
  // which reads receipt positions from it. Local dev servers only.
  if (['localhost', '127.0.0.1'].indexOf(window.location.hostname) !== -1) {
    window.__game = running;
  }
  running.load();
}

/**
 * The canvas text is drawn, not laid out by the browser, so PixiJS measures
 * the font once and keeps those measurements. Asking for it up front avoids a
 * first frame measured against the fallback and then left wrong. The page
 * preloads the file (dist/index.html, worker/page.js), so by here it is in.
 * @returns {Promise}
 */
function arcadeFontReady() {
  if (!document.fonts || !document.fonts.load) {
    return Promise.resolve();
  }
  // the start screen declares this font only once it is mounted, so the ?dog=
  // shortcut would draw the in-game text in a fallback face without this
  const face = new FontFace('Press Start 2P', `url(${arcadeFont})`);
  return face.load()
    .then(() => document.fonts.add(face))
    .catch(() => null); // a missing font shouldn't stop the game
}

document.addEventListener('DOMContentLoaded', async function() {
  await arcadeFontReady();
  const params = new URLSearchParams(window.location.search);

  // scores earlier pages finished but couldn't send go now, and keep being
  // tried while the page is open, in the background: nothing waits on them
  leaderboard.watchOutbox();

  // ?dog=<name> still skips the menu, which keeps the level creator usable
  const straightToGame = params.get('dog');
  if (straightToGame) {
    // this path shows no start screen, so a prerendered one has to come down
    // or it covers the game it skipped to (src/modules/Menu.js skip)
    Menu.skip();
    // no start screen, so the theme starts here at the in-game level
    music.setLevel('game');
    // no name unless ?name= gives one, so the start screen asks afterwards
    const loaded = await gameCode();
    startGame(loaded, {name: params.get('name') || '', dog: straightToGame});
    return;
  }

  goHome();
}, false);
